import { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useDesktopSlides } from "@/hooks/useIsDesktop";
import useSlickArrows from "@/hooks/useSlickArrows";
import { withRegionPath } from "@/utils/session";
import styles from "./CityExperiences.module.css";

function ExperienceCard({ item }) {
  const href = withRegionPath(`/place/${item.slug}`);

  return (
    <a href={href} className={styles.card}>
      <img
        src={item.thumbnailImg || item.bannerImage}
        alt={item.thumbnailAlt || item.name}
        className={styles.cardImage}
      />
      <div className={styles.overlay} />
      <p className={styles.cardLabel}>{item.name}</p>
    </a>
  );
}

function getExperiencePayload(data) {
  if (!data) return null;
  if (
    Array.isArray(data.countryGroups) ||
    Array.isArray(data.countryGroup) ||
    Array.isArray(data.items) ||
    Array.isArray(data)
  ) {
    return data;
  }
  if (data.thingsToDo) return data.thingsToDo;
  return data;
}

function getCountryGroups(payload) {
  const groups = payload?.countryGroups || payload?.countryGroup;
  if (!Array.isArray(groups)) return [];

  return groups.filter(
    (group) =>
      group?.countryName &&
      Array.isArray(group.items) &&
      group.items.length > 0
  );
}

function getDirectItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data) && !payload.data.some((row) => Array.isArray(row?.items))) {
    return payload.data;
  }
  return [];
}

export default function CityExperiences({ data = null, type }) {
  const { isDesktop, mounted, slidesToShow } = useDesktopSlides();
  const sliderRef = useRef(null);
  const [location, setLocation] = useState("");
  const payload = useMemo(() => getExperiencePayload(data), [data]);
  const countryGroups = useMemo(() => getCountryGroups(payload), [payload]);
  const directItems = useMemo(() => {
    if (countryGroups.length) return [];
    return getDirectItems(payload);
  }, [countryGroups, payload]);

  useEffect(() => {
    if (!countryGroups.length) {
      setLocation("");
      return;
    }

    setLocation((current) => {
      if (countryGroups.some((group) => group.countryName === current)) {
        return current;
      }
      return countryGroups[0].countryName;
    });
  }, [countryGroups]);

  const cities = useMemo(() => {
    if (countryGroups.length) {
      const group = countryGroups.find((item) => item.countryName === location);
      return group?.items || [];
    }
    return directItems;
  }, [countryGroups, directItems, location]);

  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    cities.length,
    slidesToShow,
    location
  );

  const handleLocationChange = (value) => {
    setLocation(value);
    if (typeof sliderRef.current?.slickGoTo === "function") {
      sliderRef.current.slickGoTo(0);
    }
  };

  const slickSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    centerMode: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
  };

  const showSlick = mounted && isDesktop;
  const fewSlides = cities.length <= slidesToShow;
  const hasItems = cities.length > 0;

  const sectionTitle =
    payload?.title || data?.title || "Find City Specific Experiences";
  const sectionSubtitle =
    payload?.subTitle || data?.subTitle || "Wherever you go, fun follows";

  return (
    <section className={`${styles.section} standard-padding`}>
      <div className={styles.header}>
        <div className={styles.headingBlock}>
          <h2 className="standard-heading">{type === "blog" ? "Popular Things to Do" : sectionTitle}</h2>
          {type === "blog" ?
            null
            :
            <p className="standard-subheading">{sectionSubtitle}</p>
          }
        </div>

        {countryGroups.length > 1 ? (
          <label className={styles.selectWrap}>
            <span className={styles.srOnly}>Select location</span>
            <select
              className={styles.locationSelect}
              value={location}
              onChange={(e) => handleLocationChange(e.target.value)}
            >
              {countryGroups.map((group) => (
                <option key={group.countryId || group.countryName} value={group.countryName}>
                  {group.countryName}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {!hasItems ? (
        <p className="nodata">No data found</p>
      ) : showSlick ? (
        <div
          className={`${styles.slickWrap} ${fewSlides ? styles.alignLeft : ""}`}
          key={location}
        >
          <button
            type="button"
            className={`headerArrowBtn ${styles.sidePrev}`}
            aria-label="Previous"
            disabled={!canPrev}
            onClick={() => {
              if (typeof sliderRef.current?.slickPrev === "function") {
                sliderRef.current.slickPrev();
              }
            }}
          >
            <MdChevronLeft size={22} />
          </button>

          <button
            type="button"
            className={`headerArrowBtn ${styles.sideNext}`}
            aria-label="Next"
            disabled={!canNext}
            onClick={() => {
              if (typeof sliderRef.current?.slickNext === "function") {
                sliderRef.current.slickNext();
              }
            }}
          >
            <MdChevronRight size={22} />
          </button>

          <Slider ref={sliderRef} {...slickSettings}>
            {cities.map((item) => (
              <div key={item.id} className={styles.slickSlide}>
                <ExperienceCard item={item} />
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <div className={styles.slider}>
          {cities.map((item) => (
            <ExperienceCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}
